import { useMutation, UseMutationOptions } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { SharedLinkResult } from '@utils/types/editors/magic-link.ts';

type ShareDocumentProps = {
  sessionId: string;
  documentId: string;
};

async function shareDocument({
  sessionId,
  documentId
}: ShareDocumentProps): Promise<SharedLinkResult> {
  const url = POST_URL(sessionId, documentId).share;
  const { data } = await Axios.post(url, { sessionId, documentId });
  return data;
}

export function useShareDocument(
  options?: UseMutationOptions<
    SharedLinkResult,
    AxiosError<BaseError>,
    ShareDocumentProps
  >
) {
  const { t } = useTranslation();

  return useMutation<
    SharedLinkResult,
    AxiosError<BaseError>,
    ShareDocumentProps
  >((props: ShareDocumentProps) => shareDocument(props), {
    onSuccess: () => {
      toast.success(t('magic-link-generated-successfully'));
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      let messages: string[] = [];

      if (Array.isArray(error?.response?.data?.detail)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        messages = error.response.data.detail.map((err: any) => {
          const field = err.loc?.[1] ?? 'field';
          return `${field}: ${err.msg}`;
        });
      } else if (error?.response?.data?.message) {
        messages = [error.response.data.message];
      } else {
        messages = [t('failed-to-generate-the-magic-link')];
      }

      toast.error(messages.join('\n'));
    },
    useErrorBoundary: false,
    ...(options ?? {})
  });
}

/* ------------------------------------------------------------------ */
/* Email share                                                        */
/* ------------------------------------------------------------------ */

type ShareDocumentEmailProps = {
  sessionId: string;
  documentId: string;
  emails: string[];
  message?: string;
};

type ShareDocumentEmailResult = {
  result: {
    link: string;
    emails_sent: string[];
    success: boolean;
  };
};

async function shareDocumentByEmail({
  sessionId,
  documentId,
  emails,
  message
}: ShareDocumentEmailProps): Promise<ShareDocumentEmailResult> {
  const shareUrl = POST_URL(sessionId, documentId).shareEmail;
  const url = `${shareUrl}`;

  const { data } = await Axios.post(url, {
    emails,
    message
  });

  return data;
}

export function useShareDocumentEmail(
  options?: UseMutationOptions<
    ShareDocumentEmailResult,
    AxiosError<BaseError>,
    ShareDocumentEmailProps
  >
) {
  const { t } = useTranslation();

  return useMutation<
    ShareDocumentEmailResult,
    AxiosError<BaseError>,
    ShareDocumentEmailProps
  >((props: ShareDocumentEmailProps) => shareDocumentByEmail(props), {
    onSuccess: data => {
      const emails = data?.result?.emails_sent ?? [];
      const count = emails.length;

      if (count === 1) {
        toast.success(
          t('magic-link-sent-to-recipient', {
            email: emails[0],
            defaultValue: `Magic link sent to ${emails[0]}`
          })
        );
      } else if (count > 1) {
        toast.success(
          t('magic-link-sent-to-recipients', {
            count,
            defaultValue: `Magic link sent to ${count} recipients`
          })
        );
      } else {
        toast.success(
          t('magic-link-email-sent', {
            defaultValue: 'Magic link email sent successfully.'
          })
        );
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      let messages: string[] = [];

      if (Array.isArray(error?.response?.data?.detail)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        messages = error.response.data.detail.map((err: any) => {
          const field = err.loc?.[1] ?? 'field';
          return `${field}: ${err.msg}`;
        });
      } else if (error?.response?.data?.message) {
        messages = [error.response.data.message];
      } else {
        messages = [t('failed-to-send-magic-link-email')];
      }

      toast.error(messages.join('\n'));
    },
    useErrorBoundary: false,
    ...(options ?? {})
  });
}
